<template>
    <div>
        <table class="kgr-table kgr-table--striped table-groups kgr-row classifications">
            <thead>
                <tr class="kgr-table__row">
                    <th
                        v-t="'memberClassification'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th
                        v-t="'Start'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th
                        v-t="'Ende'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th
                        v-t="'Bemerkungen'"
                        class="kgr-table__cell kgr-table__cell--headline notice-cell"
                    ></th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <template v-for="classification in classifications">
                    <kgr-member-classification-edit-row
                        :classification="classification"
                        :edit-classifications="editClassifications"
                        @remove="remove(classification)"
                    />

                    <kgr-member-classification-show-row
                        :classification="classification"
                        :edit-classifications="editClassifications"
                        :is-deletable="isDeletable"
                        :is-editable="isEditable"
                        @edit="edit(classification)"
                        @remove="remove(classification)"
                    />
                </template>

                <tr
                    v-if="isCreatable"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <kgr-member-classification-select
                            v-model="newClassification.parameterValue"
                            :club-id="clubId"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <div
                            class="form-widget without-label date-picker"
                            :class="{ invalid: !newClassification.isEmpty() && (!newClassification.start || !newClassification.isValidDateRange()) }"
                        >
                            <kgr-date-picker v-model="newClassification.start" />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <div
                            class="form-widget without-label date-picker"
                            :class="{ invalid: !newClassification.isEmpty() && !newClassification.isValidDateRange() }"
                        >
                            <kgr-date-picker v-model="newClassification.end" />
                        </div>
                    </td><td class="kgr-table__cell notice-cell">
                        <div class="form-widget without-label date-picker">
                            <textarea
                                v-model.trim="newClassification.notice"
                                class="kgr-input"
                                rows="3"
                            ></textarea>
                        </div>
                    </td>
                    <td class="kgr-table__cell">
&nbsp;
                    </td>
                </tr>
            </tbody>
        </table>
        <kgr-button
            v-if="isEditable || isCreatable"
            class="button_save"
            :value="$t('saveChanges')"
            :disabled="!isSavable()"
            :pending="pendingCreate || pendingUpdate"
            @click="save()"
        />
    </div>
</template>

<script>
import DatePicker from '../../common/form/picker/DatePicker';
import KgrButton from '../../common/form/button/KgrButton';
import MemberClassification from '../../../js/dto/MemberClassification';
import MemberClassificationEditRow from './MemberClassificationEditRow';
import MemberClassificationSelect from './MemberClassificationSelect';
import MemberClassificationShowRow from './MemberClassificationShowRow';
import MemberRepository from '../../../js/repository/MemberRepository';

export default {
    components: {
        'kgr-date-picker': DatePicker,
        'kgr-member-classification-select': MemberClassificationSelect,
        'kgr-member-classification-edit-row': MemberClassificationEditRow,
        'kgr-member-classification-show-row': MemberClassificationShowRow,
        'kgr-button': KgrButton,
    },
    props: {
        classifications: {
            type: Array,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
        isDeletable: {
            type: Boolean,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            newClassification: new MemberClassification(),
            editClassifications: [],
            pendingCreate: false,
            pendingUpdate: false,
        };
    },
    methods: {
        isSavable() {
            if (this.editClassifications.length === 0 && this.newClassification.isEmpty()) {
                return false;
            }

            const hasInvalidEdits = this.editClassifications.filter((editClassification) => !editClassification.isValid()).length > 0;
            if (hasInvalidEdits) {
                return false;
            }

            if (!this.newClassification.isEmpty() && !this.newClassification.isValid()) {
                return false;
            }

            return true;
        },
        remove(classification) {
            showConfirm(this.$t('confirm'), this.$t('memberClassification.confirm.delete', { name: classification.parameterValue.value }), () => {
                this.editClassifications = this.editClassifications.filter((r) => r.id !== classification.id);
                MemberRepository
                    .removeMemberClassification(this.memberId, classification.id)
                    .then(() => {
                        this.$notify.success('memberClassification.delete.success', { name: classification.parameterValue.value });
                        this.emitUpdated();
                    })
                    .catch(() => {
                        this.$notify.error('saveChanges.error');
                    });
            }, 'Ok');
        },
        edit(classification) {
            this.editClassifications.push(classification);
        },
        save() {
            if (!this.newClassification.isEmpty()) {
                this.create();
            }

            if (this.editClassifications.length > 0) {
                this.update();
            }
        },
        create() {
            this.pendingCreate = true;
            MemberRepository
                .createMemberClassification(this.memberId, this.newClassification)
                .then(() => {
                    this.newClassification = new MemberClassification();
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch((error) => {
                    if (error.status === 409) {
                        this.$notify.error('memberClassification.saveChanges.conflict', { name: this.newClassification.parameterValue.value });
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                })
                .finally(() => this.pendingCreate = false);
        },
        update() {
            this.pendingUpdate = true;
            MemberRepository
                .updateMemberClassifications(this.memberId, this.editClassifications)
                .then(() => {
                    this.editClassifications = [];
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pendingUpdate = false);
        },
        emitUpdated() {
            this.$emit('updated');
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .button_save {
    display: block;
    margin-left: auto;
}

div .date-picker {
    border: 1px solid transparent;

    &.invalid {
        border: 1px solid #f44336;
    }
}

.kgr-table__cell {
    vertical-align: top;
}

.notice-cell {
    width: 60%;
}
</style>
